<template>
  <v-container
    fluid
    class="pa-7">
    <HeaderComponent
      class="pb-4"
      :title="$vuetify.lang.t('$vuetify.shipments.title')" />

    <!-- FILE UPLOADS SECTION -->
    <v-row class="pt-5">
      <v-col
        cols="lg-4"
        offsetLg="4">
        <form
          class="d-flex justify-stretch gap-1 mb-6"
          @submit.prevent="createAttachments">
          <v-file-input
            ref="multiFileInput"
            v-model="filesToUpload"
            :label="$vuetify.lang.t('$vuetify.labels.uploadFiles')"
            outlined
            hideDetails
            multiple
            clearable
            accept="application/pdf"
            :prependIcon="null" />
          <v-btn
            height="auto"
            color="primary"
            type="submit"
            :disabled="isUploading || !filesToUpload || !filesToUpload.length">
            <v-icon v-if="!isUploading">
              $vuetify.icons.upload
            </v-icon>
            <v-progress-circular
              v-else
              indeterminate
              color="white" />
          </v-btn>
        </form>
      </v-col>
    </v-row>

    <!-- PROCESSING FILES LOADER -->
    <div
      v-if="fileIsProcessing"
      class="text-center mx-auto mt-5">
      <v-progress-circular
        :size="75"
        indeterminate
        color="primary" />
      <p class="d-block mt-5 my-10">
        {{ $vuetify.lang.t('$vuetify.messages.filesAreProcessing') }}
      </p>
    </div>

    <v-row v-else-if="attachments && attachments.length">
      <v-col
        v-for="file in attachments"
        :key="file.id"
        cols="12"
        md="6"
        lg="4"
        offsetLg="4">
        <v-card @click="showDetail(file.id)">
          <v-card-title class="d-flex justify-space-between">
            <span :class="{ 'success--text': checkedFiles.includes(file.id)}">
              {{ file.name }}
            </span>
            <v-icon
              v-if="checkedFiles.includes(file.id)"
              class="ml-3"
              color="success">
              $vuetify.icons.checkCircle
            </v-icon>
            <v-spacer />
            <v-icon>
              $vuetify.icons.detail
            </v-icon>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { EcoChallengeFilesApiService } from "@/common/services/eco-challenge-files.api.service";
import { AttachmentsApiService } from "@/common/services/attachments.api.service";
import { UploadsApiService } from "@/common/services/uploads.api.service";
import HeaderComponent from "@/components/layout/HeaderComponent.vue";
import removeFileExtension from "@/common/helpers/removeFileExtension";
import FILE_STATUS from "@/common/enums/fileStatus";
import {mapActions, mapGetters} from "vuex";

export default {
  name: 'UploadFileComponent',

  components: { HeaderComponent },

  data() {
    return {
      isUploading: false,
      /**
       * An array of files to be uploaded.
       * @type {Array<File>}
       */
      filesToUpload: [],
      // Initialize an object to track intervals for each file
      fileIntervals: {},
      // Initialize an object to track the status of each file
      fileStatuses: {},
      fileIsProcessing: false,
      FILE_STATUS,
    }
  },

  computed: {
    ...mapGetters({
      file: "files/getItem",
      files: "files/getAllItems",
      attachments: "files/getAllAttachments",
      checkedFiles: "files/getCheckedFiles",
    }),
  },

  beforeDestroy() {
    // Iterate over the keys (fileIds) in the fileIntervals object
    for (const fileId in this.fileIntervals) {
      // Clear the interval for this specific fileId
      clearInterval(this.fileIntervals[fileId]);
    }
  },

  methods: {
    ...mapActions({
      createFile: "files/createItem",
      setAttachments: "files/setAttachments",
    }),

    /**
     * Create attachments from uploaded files.
     */
    async createAttachments() {
      await this.setAttachments([]);
      const createPromises = [];
      this.isUploading = true;

      for (let i = 0, filesLength = this.filesToUpload.length; i < filesLength; i++) {
        const file = this.filesToUpload[i];

        try {
          // create the attachment
          const attachmentsResponse = await AttachmentsApiService.post({
            name: removeFileExtension(file.name),
            type: file.type,
            size: file.size,
          });
          const attachment = attachmentsResponse.getItem();

          // clear the interval for the current fileId if it exists
          if (this.fileIntervals[attachment.id]) {
            clearInterval(this.fileIntervals[attachment.id]);
          }

          // upload the file to the server
          await UploadsApiService.uploadFile(attachmentsResponse, file);

          // assign the attachment to a new product attachment
          await this.createFile({
            attachment_id: attachment.id,
            name: attachment.name,
          });

          createPromises.push(this.file);
        } catch (error) {
          this._handleError(error);
        }
      }

      // wait for all the attachments to be created and only then reset the file input
      try {
        await this.setAttachments(await Promise.all(createPromises));
        // show a success message
        this.$alertSuccess(this.$vuetify.lang.t('$vuetify.messages.uploadSuccess'));
        // reset the file input after a successful upload
        this.$refs.multiFileInput.reset();
        // reset uploading state
        this.isUploading = false;
        // fetch the attachments again to update the table
        for (const attachment of this.attachments) {
          this.pollStatus(attachment.id)
        }
      } catch (error) {
        this._handleError(error);
      }
    },

    /**
     * Poll the processing status of a file.
     * @param {number} fileId - The ID of the file to poll status for.
     */
    pollStatus(fileId) {
      // initialize status for this file as false
      this.$set(this.fileStatuses, fileId, false);
      this.fileIsProcessing = true;

      try {
        // create an interval for the specific fileId
        this.fileIntervals[fileId] = setInterval(async () => {
          const file = (await EcoChallengeFilesApiService.get(fileId)).getItem();

          if (file.status === FILE_STATUS.PROCESSED) {
            // clear the interval for this fileId
            clearInterval(this.fileIntervals[fileId]);

            // set status for this file as true when processed
            this.$set(this.fileStatuses, fileId, true);

            // check if all files have been processed
            const allFilesProcessed = Object.values(this.fileStatuses).every(status => status);
            if (allFilesProcessed) {
              // Set fileIsProcessing to false when all files are processed
              this.fileIsProcessing = false;
            }
          }
        }, 10000);
      } catch (error) {
        this._handleError(error);
      }
    },

    showDetail(fileId) {
      this.$router.push({ name: 'files.detail', params: { id: fileId } });
    },

    _handleError(error) {
      if (error.response.status === 429) {
        this.$alertError(this.$vuetify.lang.t('$vuetify.messages.APICallsNumberExceeded'));
      } else if (error.response.data.errors) {
        this.$alertError(error.response.data.errors[0].message);
      } else {
        this.$alertError(error.response.data.message);
      }
    },
  }
}
</script>

<style>
.bg-deep-purple-lighten-4 {
  background-color: #d1c4e9;
}
</style>
